import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { mmsGetJobs, mmsLogout, mmsUpdateLocation, mmsJobResume } from '../../../../services/mmsApi';
import { mmsSocketService } from '../../../../services/mmsSocket';
import { locationService } from '../../../../services/locationService';

// Custom marker icon - blue pin with tool icon
// Uses local assignment statuses: 'active', 'in_progress', 'completed', 'paused', 'reassigned'
const createCustomIcon = (status) => {
  // Normalize status for comparison
  const normalizedStatus = (status || '').toLowerCase().replace(/[_\s-]/g, '');
  
  const colors = {
    'active': '#EAB308', // Yellow for new/assigned
    'inprogress': '#3B82F6', // Blue for in progress
    'completed': '#22C55E', // Green for completed
    'paused': '#F97316', // Orange for paused
    'onhold': '#F97316', // Orange (legacy)
    'cancelled': '#EF4444', // Red
    'reassigned': '#9CA3AF' // Gray
  };
  
  const color = colors[normalizedStatus] || '#3B82F6';
  const isCompleted = normalizedStatus === 'completed';
  
  const svgIcon = `
    <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
      <!-- Shadow -->
      <ellipse cx="20" cy="48" rx="8" ry="2" fill="rgba(0,0,0,0.2)"/>
      
      <!-- Pin background -->
      <path d="M20 0 C 8 0, 0 8, 0 20 C 0 28, 8 36, 20 50 C 32 36, 40 28, 40 20 C 40 8, 32 0, 20 0 Z" 
            fill="${color}" stroke="white" stroke-width="2"/>
      
      ${isCompleted ? `
      <!-- Checkmark icon for completed -->
      <g transform="translate(20, 20)">
        <path d="M -8 -4 L -2 2 L 8 -8" 
              fill="none" 
              stroke="white" 
              stroke-width="3.5" 
              stroke-linecap="round" 
              stroke-linejoin="round"/>
      </g>
      ` : `
      <!-- Tool/wrench icon for other statuses -->
      <g transform="translate(20, 18) scale(0.8)">
        <path d="M -6 -6 L -6 -2 L -8 0 L -6 2 L -4 0 L -2 2 L 2 -2 L 0 -4 L 2 -6 L 0 -8 L -2 -6 L -6 -6 Z M 3 3 L 6 6 L 8 4 L 5 1 L 3 3 Z" 
              fill="white" stroke="none"/>
      </g>
      `}
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-job-marker',
    iconSize: [40, 50],
    iconAnchor: [20, 50],
    popupAnchor: [0, -50]
  });
};

// Component to fit map bounds to markers - only on initial load
const FitBounds = ({ jobs, shouldFitBounds }) => {
  const map = useMap();
  
  useEffect(() => {
    // Only fit bounds if explicitly requested (initial load)
    if (shouldFitBounds && jobs && jobs.length > 0 && map) {
      try {
        // Check if map container exists
        const container = map.getContainer();
        if (!container) {
          return;
        }
        
        const bounds = [];
        jobs.forEach(job => {
          const lat = job.LATITUDE || job.property_latitude;
          const lng = job.LONGITUDE || job.property_longitude;
          if (lat && lng) {
            const latNum = parseFloat(lat);
            const lngNum = parseFloat(lng);
            if (!isNaN(latNum) && !isNaN(lngNum)) {
              bounds.push([latNum, lngNum]);
            }
          }
        });
        
        if (bounds.length > 0) {
          // Use setTimeout to ensure map is fully rendered
          setTimeout(() => {
            try {
              if (map && map.getContainer()) {
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
              }
            } catch (error) {
              console.warn('Error fitting map bounds:', error);
            }
          }, 100);
        }
      } catch (error) {
        console.warn('Error in FitBounds:', error);
      }
    }
  }, [jobs, map, shouldFitBounds]);
  
  return null;
};

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  return `${distance.toFixed(1)}km`;
};

const MMSMobileJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [shouldFitBounds, setShouldFitBounds] = useState(true); // Only fit bounds on initial load
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false); // Track if initial load is done
  const [mapKey, setMapKey] = useState(0); // Key to force map recreation when needed
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'new', 'in-progress', 'paused', 'completed'

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('mms_mobile_token');
    const userData = localStorage.getItem('mms_mobile_user');
    
    console.log('🔍 MMS Mobile Jobs - Auth check:', { 
      hasToken: !!token, 
      hasUserData: !!userData,
      tokenLength: token?.length,
      currentPath: window.location.pathname
    });
    
    if (!token || !userData) {
      console.log('❌ MMS Mobile Jobs - Missing credentials, redirecting to login');
      navigate('/mms-mobile/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      console.log('✅ MMS Mobile Jobs - User data parsed successfully:', { 
        userId: parsedUser.worker_id,
        userName: parsedUser.worker_name 
      });
      setUser(parsedUser);
    } catch (error) {
      console.error('❌ MMS Mobile Jobs - Error parsing user data:', error);
      console.log('🧹 MMS Mobile Jobs - Clearing corrupted data and redirecting');
      localStorage.removeItem('mms_mobile_token');
      localStorage.removeItem('mms_mobile_user');
      navigate('/mms-mobile/login');
    }
  }, [navigate]);

  // Fetch jobs
  const fetchJobs = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // Map UI filter values to actual database status values
      const statusMapping = {
        'new': 'active',        // UI "New" -> DB "active"
        'in-progress': 'in_progress',  // UI "In Progress" -> DB "in_progress"
        'paused': 'paused',     // Same
        'completed': 'completed' // Same
      };
      
      const apiStatus = statusFilter !== 'all' 
        ? (statusMapping[statusFilter] || statusFilter)
        : null;
      
      const response = await mmsGetJobs(apiStatus);

      console.log('📥 Jobs response:', response.data);

      if (response.data && response.data.status === 1 && response.data.data) {
        const jobsData = response.data.data.jobs || [];
        setJobs(jobsData);
        
        // Only fit bounds on the very first load
        if (!isRefresh && !hasInitiallyLoaded) {
          setShouldFitBounds(true);
          setHasInitiallyLoaded(true);
        } else {
          setShouldFitBounds(false);
        }
        
        // Don't show toast on initial load, only useful info in console
        if (response.data.data.is_local_account) {
          console.log('ℹ️', response.data.data.message || 'Local testing account');
        }
      }
    } catch (error) {
      console.error('❌ MMS Mobile - Error fetching jobs:', error);
      if (error.response?.status === 401) {
        console.log('🔒 MMS Mobile - 401 Unauthorized, session expired');
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else if (!isRefresh) {
        // Only show error toast on initial load, not on background refresh
        console.error('❌ MMS Mobile - Jobs fetch failed:', error.response?.data || error.message);
        toast.error('Failed to load jobs');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasInitiallyLoaded, statusFilter]);

  // Silent auto-refresh every 30 seconds
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    fetchJobs();

    // Set up auto-refresh interval (30 seconds)
    const refreshInterval = setInterval(() => {
      // Silent refresh - don't show loading states
      fetchJobs(true);
    }, 30000); // 30 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, [user, fetchJobs]);

  // Get user's current location
  useEffect(() => {
    const getUserLocation = async () => {
      try {
        const position = await locationService.getCurrentLocation();
        if (position?.coords) {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        }
      } catch (error) {
        console.log('Could not get user location:', error);
      }
    };
    
    getUserLocation();
  }, []);

  // Connect to socket
  useEffect(() => {
    if (user) {
      const connectSocket = async () => {
        try {
          await mmsSocketService.connect(user);
          setIsOnline(true);

          // Listen for new assignments
          mmsSocketService.onAssignment((assignments) => {
            console.log('New assignments received:', assignments);
            fetchJobs(true);
            toast.info('New job assigned!');
          });

          // Listen for connection status
          mmsSocketService.onStatusChange((status) => {
            setIsOnline(status === 'connected');
          });
        } catch (error) {
          console.error('Socket connection error:', error);
        }
      };

      connectSocket();

      return () => {
        mmsSocketService.disconnect();
      };
    }
  }, [user, fetchJobs]);

  // Start location tracking
  useEffect(() => {
    if (user && isOnline) {
      const startTracking = async () => {
        try {
          const permission = await locationService.requestPermission();
          
          if (permission) {
            // Start tracking with 5-minute interval
            locationService.startTracking(
              async (position) => {
                const { latitude, longitude, accuracy } = position.coords;
                
                // Send to API
                try {
                  await mmsUpdateLocation({
                    latitude,
                    longitude,
                    accuracy,
                    timestamp: new Date().toISOString()
                  });
                } catch (error) {
                  console.error('Error sending location to API:', error);
                }

                // Send to socket
                await mmsSocketService.sendLocationUpdate(latitude, longitude);
              },
              (error) => {
                console.error('Location tracking error:', error);
                setIsTracking(false);
              },
              300000 // 5 minutes
            );
            
            setIsTracking(true);
          }
        } catch (error) {
          console.error('Error starting location tracking:', error);
        }
      };

      startTracking();

      return () => {
        locationService.stopTracking();
      };
    }
  }, [user, isOnline]);

  const handleLogout = async () => {
    console.log('🚪 MMS Mobile - Logout initiated');
    
    try {
      await mmsLogout();
      console.log('✅ MMS Mobile - Server logout successful');
    } catch (error) {
      console.error('❌ MMS Mobile - Server logout error:', error);
      // Continue with client cleanup even if server call fails
    }

    // Clean up client-side data
    locationService.stopTracking();
    mmsSocketService.disconnect();
    localStorage.removeItem('mms_mobile_token');
    localStorage.removeItem('mms_mobile_user');
    
    console.log('🧹 MMS Mobile - Client cleanup complete, redirecting to login');
    navigate('/mms-mobile/login');
  };

  const handleResumeJob = async (jobId) => {
    try {
      // Call the proper resume endpoint with optional notes
      await mmsJobResume(jobId, 'Job resumed from mobile app');
      
      toast.success('Job resumed!');
      fetchJobs(); // Refresh job list
    } catch (error) {
      console.error('Error resuming job:', error);
      toast.error('Failed to resume job: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase().replace(/[_\s-]/g, '');
    switch (statusLower) {
      case 'active': // New/assigned (local status)
      case 'pending': // Legacy
      case 'assigned': // Legacy
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'inprogress':
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'onhold':
      case 'on-hold':
      case 'paused':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'cancelled':
      case 'reassigned':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  // Helper function to get job status (local or EDAMS)
  const getJobStatus = (job) => {
    // Prefer local assignment status
    if (job.status || job.assignment_status) {
      return (job.status || job.assignment_status || '').toLowerCase().replace(/[_\s-]/g, '');
    }
    
    // Fallback to EDAMS status if no local assignment
    // Map EDAMS OM_STATUS_NAME to local status equivalents
    if (job.OM_STATUS_NAME) {
      const edarnsStatus = job.OM_STATUS_NAME.toLowerCase();
      if (edarnsStatus.includes('pending') || edarnsStatus.includes('assigned')) return 'active';
      if (edarnsStatus.includes('progress')) return 'inprogress';
      if (edarnsStatus.includes('complete')) return 'completed';
    }
    
    // Default to active if no status found
    return 'active';
  };

  // Filter jobs based on selected status filter
  // Use local assignment status field (not EDAMS status)
  // Apply client-side filtering as backup in case API doesn't filter properly
  const filteredJobs = (() => {
    if (statusFilter === 'all') {
      // Show all except completed
      return jobs.filter(j => {
        const status = getJobStatus(j);
        return status !== 'completed';
      });
    }
    
    // Map UI filter to database status values
    const statusMap = {
      'new': 'active',
      'in-progress': 'in_progress',
      'paused': 'paused',
      'completed': 'completed'
    };
    
    const targetStatus = statusMap[statusFilter] || statusFilter;
    
    // Filter to show only jobs matching the selected status
    return jobs.filter(j => {
      const status = getJobStatus(j);
      return status === targetStatus.toLowerCase().replace(/[_\s-]/g, '');
    });
  })();

  // Calculate status counts using local assignment statuses
  // Local statuses: 'active' (new/assigned), 'in_progress', 'paused', 'completed', 'reassigned'
  const statusCounts = {
    pending: jobs.filter(j => {
      const status = getJobStatus(j);
      return status === 'active'; // 'active' means assigned but not started
    }).length,
    assigned: 0, // Legacy - not used in local statuses
    inProgress: jobs.filter(j => {
      const status = getJobStatus(j);
      return status === 'inprogress';
    }).length,
    paused: jobs.filter(j => {
      const status = getJobStatus(j);
      return status === 'paused';
    }).length,
    completed: jobs.filter(j => {
      const status = getJobStatus(j);
      return status === 'completed';
    }).length
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Header - Matching Mobile App Style with Gradient */}
      <div 
        className="text-white sticky top-0 z-20" 
        style={{ 
          background: 'linear-gradient(135deg, #0099E0 0%, #0086D4 100%)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        }}
      >
        
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Title and User */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-xl font-bold text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>My Jobs</h1>
                  <p className="text-xs text-white opacity-75 mt-0.5 truncate">
                    {user?.worker_name || user?.full_name || 'Tester'}
                  </p>
                </div>

              </div>
            </div>
            
            {/* Right: Status Badges & Logout */}
            <div className="flex items-center gap-2 ml-4">
              {isOnline && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white bg-opacity-25 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                  <span className="text-xs font-bold">Online</span>
                </div>
              )}
              
              {isTracking && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-white bg-opacity-25 rounded-full">
                  <span className="text-xs">📍</span>
                  <span className="text-xs font-bold">Track</span>
                </div>
              )}
              
              <button
                onClick={handleLogout}
                className="ml-1 p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Logout"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>

                        {/* Status Counters */}
                <div className="flex items-center gap-1.5 py-2 justify-center">
                  <div className="flex items-center gap-1 px-2 py-1 bg-opacity-15 rounded-lg border border-white border-opacity-30">
                    <span className="text-sm font-bold text-white">{statusCounts.pending}</span>
                    <span className="text-xs text-white opacity-90">New</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-opacity-15 rounded-lg border border-white border-opacity-30">
                    <span className="text-sm font-bold text-white">{statusCounts.inProgress}</span>
                    <span className="text-xs text-white opacity-90">In Progress</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-opacity-15 rounded-lg border border-white border-opacity-30">
                    <span className="text-sm font-bold text-white">{statusCounts.paused}</span>
                    <span className="text-xs text-white opacity-90">Paused</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-opacity-15 rounded-lg border border-white border-opacity-30">
                    <span className="text-sm font-bold text-white">{statusCounts.completed}</span>
                    <span className="text-xs text-white opacity-90">Done</span>
                  </div>
                </div>
      </div>

      {/* Jobs List */}
      <div className="max-w-2xl mx-auto p-4">
        {/* Status Filters */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('new')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'new'
                ? 'bg-yellow-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            New
          </button>
          <button
            onClick={() => setStatusFilter('in-progress')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'in-progress'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setStatusFilter('paused')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'paused'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Paused
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'completed'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Completed
          </button>
        </div>

        {/* Map/List Toggle */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => {
              if (!showMap) {
                setMapKey(prev => prev + 1); // Force map recreation when switching to map view
              }
              setShowMap(true);
            }}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showMap 
                ? 'bg-blue-600 text-white' 
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Map View
          </button>
          <button
            onClick={() => setShowMap(false)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !showMap 
                ? 'bg-blue-600 text-white' 
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            List View
          </button>
        </div>

        {/* Map View */}
        {showMap && (
          <div className="mb-4 bg-white rounded-lg shadow-sm overflow-hidden" style={{ height: '400px' }} key="map-container">
            {filteredJobs.length > 0 ? (
              <MapContainer
                key={`mms-mobile-jobs-map-${mapKey}`}
                center={[-25.4653, 30.9702]} // Mbombela/Nelspruit coordinates
                zoom={12}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                whenReady={(map) => {
                  // Map is ready - ensure container exists and is still mounted
                  // Note: We don't call invalidateSize here as it can cause errors
                  // when the map is unmounting or DOM elements are not ready
                  try {
                    if (!map || !map.target) {
                      return;
                    }
                    
                    const container = map.target.getContainer();
                    if (!container) {
                      return;
                    }
                    
                    // Check if container is still in DOM
                    if (!document.body.contains(container)) {
                      return;
                    }
                    
                    // Map is ready and valid - no need to invalidate size
                    // Leaflet will handle sizing automatically
                  } catch (error) {
                    // Silently ignore - map might be unmounting
                    console.debug('Map whenReady error:', error);
                  }
                }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Fit bounds to show all markers (only on initial load) */}
                {filteredJobs.length > 0 && (
                  <FitBounds jobs={filteredJobs} shouldFitBounds={shouldFitBounds} />
                )}
                
                {/* User location marker (if available) */}
                {userLocation && (
                  <Marker
                    key="user-location"
                    position={[userLocation.lat, userLocation.lng]}
                    icon={L.divIcon({
                      html: `
                        <div style="
                          width: 20px;
                          height: 20px;
                          background: #3B82F6;
                          border: 3px solid white;
                          border-radius: 50%;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                        "></div>
                      `,
                      className: 'user-location-marker',
                      iconSize: [20, 20],
                      iconAnchor: [10, 10]
                    })}
                  >
                    <Popup>
                      <div className="text-sm font-semibold">Your Location</div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Job markers */}
                {filteredJobs
                  .filter((job) => {
                    const lat = job.LATITUDE || job.property_latitude;
                    const lng = job.LONGITUDE || job.property_longitude;
                    if (!lat || !lng) return false;
                    const jobLat = parseFloat(lat);
                    const jobLng = parseFloat(lng);
                    return !isNaN(jobLat) && !isNaN(jobLng) && 
                           jobLat >= -90 && jobLat <= 90 && 
                           jobLng >= -180 && jobLng <= 180;
                  })
                  .map((job) => {
                    const lat = job.LATITUDE || job.property_latitude;
                    const lng = job.LONGITUDE || job.property_longitude;
                    const jobLat = parseFloat(lat);
                    const jobLng = parseFloat(lng);
                    
                    // Calculate distance from user
                    let distance = null;
                    if (userLocation) {
                      distance = calculateDistance(userLocation.lat, userLocation.lng, jobLat, jobLng);
                    }
                    
                    return (
                      <Marker
                        key={`job-${job.assignment_id || job.id || `job-${jobLat}-${jobLng}`}`}
                        position={[jobLat, jobLng]}
                        icon={createCustomIcon(getJobStatus(job))}
                      >
                      <Popup maxWidth={250}>
                        <div className="text-sm" style={{ minWidth: '200px' }}>
                          {/* Job Header */}
                          <div className="font-bold text-base mb-2 text-blue-600">
                            {job.jobcard_no || `Job #${job.assignment_id}`}
                          </div>
                          
                          {/* Status Badge */}
                          <div className="mb-2">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              getJobStatus(job) === 'completed' ? 'bg-green-100 text-green-700' :
                              getJobStatus(job) === 'inprogress' ? 'bg-blue-100 text-blue-700' :
                              getJobStatus(job) === 'active' ? 'bg-yellow-100 text-yellow-700' :
                              getJobStatus(job) === 'paused' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {(job.status || job.assignment_status || job.OM_STATUS_NAME || 'Pending')?.replace(/_/g, ' ').replace(/-/g, ' ').toUpperCase()}
                            </span>
                          </div>
                          
                          {/* Job Description */}
                          {job.JOB_DESCRIPTION && (
                            <div className="mb-2 text-gray-800 font-medium">
                              {job.JOB_DESCRIPTION}
                            </div>
                          )}
                          
                          {/* Customer */}
                          {job.customer_name && (
                            <div className="mb-1">
                              <span className="font-semibold">Customer:</span> {job.customer_name}
                            </div>
                          )}
                          
                          {/* Application ID */}
                          {job.application_id && (
                            <div className="mb-1 text-xs text-gray-600">
                              <span className="font-semibold">App ID:</span> #{job.application_id}
                            </div>
                          )}
                          
                          {/* Address */}
                          {job.property_address && (
                            <div className="text-gray-600 text-xs mb-2 border-l-2 border-blue-400 pl-2">
                              📍 {job.property_address}
                            </div>
                          )}
                          
                          {/* Assigned Date */}
                          {job.assigned_at && (
                            <div className="mb-1 text-xs text-gray-600">
                              <span className="font-semibold">Assigned:</span> {formatDate(job.assigned_at)}
                            </div>
                          )}
                          
                          {/* Distance */}
                          {distance && (
                            <div className="mb-2 flex items-center gap-1 text-blue-600 font-semibold">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                              <span>{distance} away</span>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => navigate(`/mms-mobile/job/${job.assignment_id}`)}
                              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700"
                            >
                              View Details
                            </button>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${jobLat},${jobLng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 flex items-center justify-center gap-1"
                              title="Get Directions"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              <span>Navigate</span>
                            </a>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <p className="text-sm">No jobs to display on map</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Refresh Button */}
        <button
          onClick={() => fetchJobs(true)}
          disabled={refreshing}
          className="w-full mb-4 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh Jobs'}
        </button>

        {filteredJobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-600 font-medium">No jobs assigned</p>
            <p className="text-gray-500 text-sm mt-1">You'll see your assigned jobs here</p>
          </div>
        ) : !showMap && (
          <div className="space-y-3">
            {filteredJobs
              .sort((a, b) => {
                // Define status priority using local assignment statuses
                // 0 = urgent (active/new jobs), 1 = in progress, 2 = done/inactive
                const statusPriority = {
                  'active': 0, // New/assigned jobs (highest priority)
                  'pending': 0, // Legacy
                  'assigned': 0, // Legacy
                  'inprogress': 1, // Active work
                  'paused': 1, // Temporarily stopped
                  'onhold': 1, // Legacy
                  'completed': 2, // Finished
                  'cancelled': 2, // Cancelled
                  'reassigned': 2, // No longer assigned
                  'failed': 2 // Failed
                };
                
                const statusA = getJobStatus(a);
                const statusB = getJobStatus(b);
                
                const priorityA = statusPriority[statusA] ?? 3;
                const priorityB = statusPriority[statusB] ?? 3;
                
                return priorityA - priorityB;
              })
              .map((job) => (
              <div
                key={job.assignment_id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                {/* Job Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {job.jobcard_no || `Job #${job.assignment_id}`}
                    </h3>
                    {job.application_id && (
                      <p className="text-sm text-gray-600">App #{job.application_id}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(getJobStatus(job))}`}>
                    {(job.status || job.assignment_status || job.OM_STATUS_NAME || 'Pending')?.replace(/_/g, ' ').replace(/-/g, ' ')}
                  </span>
                </div>

                {/* Job Description */}
                {job.JOB_DESCRIPTION && (
                  <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm font-medium text-gray-900">{job.JOB_DESCRIPTION}</p>
                  </div>
                )}

                {/* Job Details */}
                <div className="space-y-2 text-sm">
                  {job.customer_name && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>{job.customer_name}</span>
                    </div>
                  )}
                  
                  {job.property_address && (
                    <div className="flex items-start gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="flex-1">{job.property_address}</span>
                    </div>
                  )}

                  {job.assigned_at && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Assigned: {formatDate(job.assigned_at)}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {job.assignment_notes && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600 italic">
                      Note: {job.assignment_notes}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-3 flex gap-2">
                  {(job.assignment_status === 'paused' || job.assignment_status === 'on-hold') ? (
                    <>
                      <button
                        onClick={() => handleResumeJob(job.assignment_id)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <span>▶</span>
                        <span>Resume</span>
                      </button>
                      <button
                        onClick={() => navigate(`/mms-mobile/job/${job.assignment_id}`)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        View
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => navigate(`/mms-mobile/job/${job.assignment_id}`)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      View Details
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MMSMobileJobs;
